import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { changeCleaning, CleaningAction, CleaningResult } from '../../../../api/stays-alerts-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { StayFull } from '../../../../interfaces/stays';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import toast from '../../../../utils/toast';
import { formatMoney } from '../../../../utils/stays-money';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { duration, easing, layout, radius, shadow, spacing, status, typography } from '../../../../style-utils/theme';

/**
 * THE CLEANING PILL (plan S8, SC-2; R-S8.15). A button dressed as a status
 * pill that walks the cycle Add cleaning → Cleaning booked → Cleaned; a press
 * on "Cleaned" asks: back to booked, remove the cleaning, or never mind.
 * The server keeps S4's shape (an agency cleaning carries a fee — the
 * listing's default when none is given; a self cleaning has none). Hidden on
 * cancelled and no-show stays (the caller decides).
 */

type Tone = 'neutral' | 'brand' | 'success';

const Pill = styled.button<{ $tone: Tone }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.compactControlHeight};
  padding: ${spacing.half} ${spacing.sm};
  border: 1px solid transparent;
  border-radius: ${radius.pill};
  background: ${p => status[p.$tone].bg};
  color: ${p => status[p.$tone].fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: box-shadow ${duration.fast} ${easing.standard}, transform ${duration.fast} ${easing.standard};

  &::before {
    content: '';
    width: ${spacing.xs};
    height: ${spacing.xs};
    border-radius: ${radius.circle};
    background: ${p => status[p.$tone].dot};
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${shadow.sm};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: progress;
    opacity: 0.62;
  }
`;

export const cleaningPillWords = (
  s: Pick<StayFull, 'cleaning' | 'money'>
): { tone: Tone; text: string; next: string } => {
  const c = s.cleaning;
  // A co-host's stay has no `money` and no fee (IS5): the pill names no amount.
  const fee = c && c.fee_cents != null && s.money ? ` · ${formatMoney(c.fee_cents, s.money.currency)}` : '';
  if (!c || c.status === 'unscheduled') return { tone: 'neutral', text: 'Add cleaning', next: 'Press to book a cleaning' };
  if (c.status === 'completed') return { tone: 'success', text: `Cleaned${fee}`, next: 'Press for options' };

  return { tone: 'brand', text: `Cleaning booked${fee}`, next: 'Press to mark cleaned' };
};

/** The stay as it now is, from the cleaning route's answer. */
export const withCleaning = (stay: StayFull, r: CleaningResult): StayFull => ({
  ...stay,
  version: r.version,
  cleaning: { ...stay.cleaning, status: r.cleaning_status, type: r.cleaning_type, fee_cents: r.cleaning_fee_cents },
});

export const CleaningPill: React.FC<{ stay: StayFull; onChanged: (next: StayFull) => void }> = ({ stay, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const running = useRef(false);
  const first = stay.lead.first_name || 'the guest';
  const words = cleaningPillWords(stay);
  const state = stay.cleaning.status === 'unscheduled' ? 'not booked' : stay.cleaning.status === 'completed' ? 'cleaned' : 'booked';

  const run = async (action: CleaningAction) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const r = await changeCleaning(stay.id, { action, version: stay.version });
      onChanged(withCleaning(stay, r));
    } catch (e) {
      toast.error(refusalText(e as StaysApiError, 'Could not change the cleaning. Try again.'));
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const press = () => {
    if (stay.cleaning.status === 'unscheduled') void run('schedule');
    else if (stay.cleaning.status === 'scheduled') void run('complete');
    else setAsking(true);
  };

  return (
    <>
      <Pill
        type="button"
        $tone={words.tone}
        disabled={busy}
        aria-busy={busy || undefined}
        aria-label={`Cleaning for ${first}'s stay: ${state}. ${words.next}`}
        data-cleaning={stay.cleaning.status}
        onClick={press}
      >
        {words.text}
      </Pill>
      <ConfirmDialog
        open={asking}
        title={`Cleaning is marked done for ${first}'s stay`}
        message="Choose what to change."
        primaryLabel="Back to booked"
        onPrimary={async () => {
          await run('reopen');
          setAsking(false);
        }}
        secondaryLabel="Remove cleaning"
        onSecondary={() => {
          setAsking(false);
          void run('unassign');
        }}
        dismissLabel="Never mind"
        onDismiss={() => setAsking(false)}
      />
    </>
  );
};
